import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import FormField from "../../../../components/form-field/FormField";
import { RequiredValidatorModel } from "../../../../services/form/validators/required/RequiredValidator";
import { CustomDropdownOptionsValidatorModel } from "../../../../services/form/validators/custom-dropdown/CustomDropdownOptionsValidator";
import { translationService } from "../../../../services/translation/TranslationService";
import { DetailType, DetailTypeTranslations } from "./DetailType";
import "../ProjectProperties.scss";
import { CustomProjectPropertyOptionResponse } from "../../../../api-models/response/CustomProjectPropertyValueResponse";
import { CustomProperty } from "../../../../components/data-components/CustomProperty";
import { Dropdown, DropdownOption } from "../../../../components/dropdown/Dropdown";
interface ProjectPropertyLabelProps {
  label?: string;
  customProjectProperty?: CustomProperty;
}

export function DisplayProjectPropertyLabel(props: ProjectPropertyLabelProps) {
  const label = props.customProjectProperty?.label;

  return (
    <FormField
      id="project-property-label"
      labelKey="custom-project-properties.edit.label"
      readOnly
      fieldValue={label}
    />
  );
}

export function EditProjectPropertyLabel(props: ProjectPropertyLabelProps) {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  return (
    <FormField
      id="detail-name"
      className="input"
      labelKey="custom-project-properties.edit.label"
      required
      component="input"
      fieldName="label"
      fieldValue={props.label}
      validator={requiredValidator}
      placeholderKey="custom-project-properties.edit.label.placeholder"
      maxLength={100}
      errorName="label"
    />
  );
}

interface DisplayProjectPropertyTypeProps {
  customProjectProperty?: CustomProperty;
}

export const DisplayProjectPropertyType = observer((props: DisplayProjectPropertyTypeProps) => {
  const getTypeTranslation = useCallback((type: number) => {
    const typeKey = Object.keys(DetailType).find((key) => DetailType[key] === type) || "";
    return DetailTypeTranslations[typeKey];
  }, []);

  const dropdownOptions = useMemo(() => {
    return (
      <div className="description-fields">
        <div>
          <label className="label-light dropdown-values-label">
            {translationService.translate("custom-project-properties.edit.dropdown-values")}
          </label>
        </div>

        {props.customProjectProperty?.options.map(
          (customOption: CustomProjectPropertyOptionResponse) => (
            <div>{customOption.name}</div>
          )
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customProjectProperty?.options]);
  return (
    <>
      <FormField
        id="project-property-type"
        labelKey="custom-project-properties.edit.type.label"
        readOnly
        fieldValue={translationService.translate(
          getTypeTranslation(Number(props.customProjectProperty?.type))
        )}
      />
      {props.customProjectProperty?.isOfTypeDropdown() && dropdownOptions}
    </>
  );
});

interface EditProjectPropertyTypeProps {
  type?: DropdownOption;
  customOptions?: CustomProjectPropertyOptionResponse[];
  setFieldValue?: Function;
}

export const EditProjectPropertyType = observer((props: EditProjectPropertyTypeProps) => {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const [requiredUniqueDropdownOptionsValidator] = useState(() =>
    CustomDropdownOptionsValidatorModel.create({
      areOptionsRequired: true,
      areOptionsUnique: true,
    })
  );

  const propertyIsDropdown = useMemo(() => {
    return Number(props.type?.id) === Number(DetailType.Dropdown);
  }, [props.type]);

  const options = Object.entries(DetailType).map((option) => {
    return {
      id: option[1],
      name: translationService.translate(DetailTypeTranslations[option[0]]),
    };
  });

  const dropdownOptions = useMemo(() => {
    return (
      <FormField
        id="custom-dropdown-options"
        className="textarea"
        labelKey={"custom-project-properties.edit.dropdown-values"}
        fieldName="customOptions"
        fieldValue={props.customOptions || ""}
        component="textarea"
        placeholderKey={"custom-project-properties.edit.dropdown-values.placeholder"}
        maxLength={1000}
        required
        validator={propertyIsDropdown ? requiredUniqueDropdownOptionsValidator : requiredValidator}
        errorName="customOptions"
      />
    );
  }, [
    propertyIsDropdown,
    props.customOptions,
    requiredUniqueDropdownOptionsValidator,
    requiredValidator,
  ]);

  return (
    <>
      <FormField
        id="detail-type"
        className="select"
        labelKey="custom-project-properties.edit.type.label"
        required
        fieldName="type"
        fieldValue={props.type}
        errorName="type"
        validator={requiredValidator}
        children={() => (
          <Dropdown
            selectedOption={props.type ?? null}
            options={options}
            fieldName="type"
            setFieldValue={props.setFieldValue}
          />
        )}
      />
      {propertyIsDropdown && dropdownOptions}
    </>
  );
});
